import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../hooks/use-debounce";
import { cn, interval } from "../lib/utils";
import { Badge } from "./badge";
import { Checkbox } from "./checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./command";
import { Icon, type IconSymbol } from "./icon";
import { InlineFeedback, InlineFeedbackGroup } from "./inline-feedback";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Skeleton } from "./skeleton";
import { TextField } from "./textfield";
import { TruncatedText } from "./truncated-text";
import { Typography } from "./typography";

export interface Option<T> {
  value: T;
  label: string;
  badge?: string;
  description?: string;
}
export type SearchableSelectOptions<T> = Option<T>[];
interface Section<T> {
  title: string;
  options: Option<T>[];
}
export type SearchableSelectSections<T> = Section<T>[];

export interface SearchableSelectProps<T> {
  sections?: SearchableSelectSections<T>;
  multi?: boolean;
  options?: SearchableSelectOptions<T>;
  value?: T[];
  defaultValue?: T[];
  onSelect?: (value: T[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  leftIcon?: IconSymbol;
  noResultsText?: string;
  onSearch?: (searchValue: string) => void;
  error?: FieldError;
  helperText?: string;
  variant?: "default" | "ghost";
  size?: "default" | "small";
  optionRenderer?: OptionRenderer<T>;
  isLoading?: boolean;
  onOpenChange?: (open: boolean) => void;
  loadingRenderer?: LoadingRenderer;
  displayValue?: string | ReactNode; // Display value when options aren't loaded yet
  readonly?: boolean;
  footerRenderer?: FooterRenderer;
  popoverContentAlign?: "start" | "end" | "center";
  dataTestId?: string;
  searchInputTestId?: string;
}

export type OptionRendererProps<T> =
  | {
      option: Option<T>;
      isMulti: boolean;
      readonly: true;
      onSelect?: () => void;
      isSelected?: boolean;
    }
  | {
      option: Option<T>;
      isSelected: boolean;
      isMulti: boolean;
      onSelect: () => void;
      readonly?: false;
    };
export type OptionRenderer<T> = React.ComponentType<OptionRendererProps<T>>;

export type LoadingRenderer = React.ComponentType;

export type FooterRenderer = React.ComponentType;

function useSearchableSelectOptions<T>({
  options: _options,
  onSearch: _onSearch,
}: {
  options: SearchableSelectOptions<T> | undefined;
  onSearch: SearchableSelectProps<T>["onSearch"];
}) {
  const options = useMemo<SearchableSelectOptions<T>>(() => {
    return _options ?? [];
  }, [_options]);

  const [filteredOptions, setFilteredOptions] = useState<
    SearchableSelectOptions<T>
  >([]);

  const onSearchDefault = useCallback(
    (searchValue: string) => {
      if (!searchValue || searchValue === "") {
        setFilteredOptions(options);
        return;
      }

      const result = options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
          option.description?.toLowerCase().includes(searchValue.toLowerCase()),
      );
      setFilteredOptions(result);
    },
    [options],
  );

  const onSearch = _onSearch ?? onSearchDefault;

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  return {
    options: filteredOptions,
    onSearch,
  };
}
const DefaultOptionRenderer = <T,>({
  option,
  isSelected,
  isMulti,
  onSelect,
  readonly,
}: OptionRendererProps<T>) => {
  if (!option) return null;

  return (
    <div className="flex items-center justify-between w-full">
      {isMulti ? (
        <div className="flex items-center">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelect}
            className="mr-2"
          />
          <TruncatedText>{option.label}</TruncatedText>
        </div>
      ) : (
        <>
          <div>
            <TruncatedText>{option.label}</TruncatedText>
            {option.description && (
              <Typography
                variant="caption"
                className="text-gray-700 normal-case font-normal"
              >
                {option.description}
              </Typography>
            )}
          </div>
          {!readonly && isSelected && (
            <Icon icon="check" className="text-primary" />
          )}
        </>
      )}
    </div>
  );
};

const SKELETON_ITEM_COUNT = 5;
// Default loading renderer - always shows {SKELETON_ITEM_COUNT} skeleton items
const DefaultLoadingRenderer: LoadingRenderer = () => {
  return (
    <>
      {interval(SKELETON_ITEM_COUNT).map((index) => (
        <CommandItem key={`loading-${index}`} disabled>
          <div className="flex items-center w-full py-1">
            <Skeleton className="h-4 w-full rounded" />
          </div>
        </CommandItem>
      ))}
    </>
  );
};
const _SearchableSelect = <T,>(
  props: SearchableSelectProps<T> & { ref?: React.Ref<HTMLButtonElement> },
) => {
  const { t } = useTranslation();
  const {
    sections,
    options,
    multi = false,
    value = [],
    defaultValue = [],
    onSelect,
    placeholder = t("select_placeholder"),
    searchPlaceholder = t("search_in_list_placeholder"),
    className = "",
    noResultsText = t("no_items"),
    onSearch,
    error,
    helperText,
    variant = "default",
    size = "default",
    optionRenderer: OptionRenderer = DefaultOptionRenderer,
    isLoading = false,
    onOpenChange,
    loadingRenderer: LoadingRenderer = DefaultLoadingRenderer,
    displayValue,
    readonly,
    footerRenderer: FooterRenderer = () => <></>,
    popoverContentAlign = "center",
    dataTestId,
    searchInputTestId,
    ref,
  } = props;
  const searchableSelectOptions = useSearchableSelectOptions({
    options,
    onSearch,
  });

  const allOptions = useMemo(() => {
    return sections
      ? sections.flatMap((section) => section.options)
      : options || [];
  }, [sections, options]);

  const selectedOptions = useMemo(() => {
    if (!value.length) {
      return defaultValue.length
        ? allOptions.filter((opt) => defaultValue.includes(opt.value))
        : [];
    }
    return allOptions.filter((opt) => value.includes(opt.value));
  }, [value, allOptions, defaultValue]);

  const [searchValue, setSearchValue] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  const debouncedSearchValue = useDebounce(searchValue, 300);

  useEffect(() => {
    searchableSelectOptions.onSearch(debouncedSearchValue);
  }, [debouncedSearchValue, searchableSelectOptions.onSearch]);

  const handleSelect = (value: string) => {
    const option = allOptions.find((opt) => String(opt.value) === value);
    if (option) {
      let newSelectedOptions: Option<T>[] = [];
      if (multi) {
        const isSelected = selectedOptions.some(
          (opt) => opt.value === option.value,
        );
        newSelectedOptions = isSelected
          ? selectedOptions.filter((opt) => opt.value !== option.value)
          : [...selectedOptions, option];
      } else {
        newSelectedOptions = [option];
      }
      onSelect?.(newSelectedOptions.map((opt) => opt.value));
    }
    if (!multi) setIsOpen(false);
  };
  const renderOption = (option: Option<T>): ReactNode => {
    const isSelected = selectedOptions.some(
      (opt) => opt.value === option.value,
    );

    return (
      <OptionRenderer
        option={option}
        isSelected={isSelected}
        isMulti={multi}
        onSelect={() => handleSelect(String(option.value))}
      />
    );
  };

  const selectedCountBadge = useMemo(() => {
    if (Array.isArray(selectedOptions) && selectedOptions.length > 0) {
      return (
        <Badge className="text-gray-700" variant={"tag"}>
          {t("selected_options", { count: selectedOptions.length })}
        </Badge>
      );
    }
  }, [selectedOptions, t]);

  const triggerDisplayValue = useMemo(() => {
    if (!multi && selectedOptions.length === 1) {
      if (!selectedOptions[0].description) {
        const Renderer = OptionRenderer || DefaultOptionRenderer;
        return (
          <Renderer
            option={selectedOptions[0]}
            isMulti={false}
            readonly={true}
          />
        );
      }

      return <TruncatedText>{selectedOptions[0].label}</TruncatedText>;
    }
    // If we have selected options from the list, use them
    if (selectedOptions.length > 0 && value.length > 0) {
      return multi ? selectedCountBadge : selectedOptions[0].label;
    }

    // If value is selected but options not loaded, use displayValue
    if (value.length === 0 && displayValue) {
      return displayValue;
    }

    // Otherwise show nothing (placeholder will show)
    return "";
  }, [
    selectedOptions,
    value,
    displayValue,
    multi,
    selectedCountBadge,
    OptionRenderer,
  ]);

  return (
    <div className="flex flex-col space-y-1">
      <Popover
        open={isOpen}
        onOpenChange={() => {
          if (readonly) return;
          setIsOpen(!isOpen);
          onOpenChange?.(!isOpen); // Notify parent component
        }}
      >
        <PopoverTrigger asChild className={cn("max-w-md", className)}>
          <SearchableSelectTriggerButton
            ref={ref}
            placeholder={placeholder}
            value={triggerDisplayValue}
            hasValue={triggerDisplayValue !== ""}
            error={error}
            variant={variant}
            isOpen={isOpen}
            size={size}
            multi={multi}
            readonly={readonly}
          />
        </PopoverTrigger>
        <PopoverContent
          align={popoverContentAlign}
          className="p-0 border border-gray-200 min-w-[var(--radix-popover-trigger-width)]"
          data-testid={dataTestId}
        >
          <Command shouldFilter={false}>
            <div className="border-b p-2 w-full">
              <TextField
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
                leftIcon="search"
                rightIcon="x"
                rightIconClick={() => setSearchValue("")}
                data-testid={searchInputTestId}
              />
            </div>
            {/* NB: We use max-h-[218px] highlighted by Youssfi, in lists we want to display max 7 items; so this is the calculation  */}
            <CommandList
              onWheel={(e) => {
                // Allow scrolling inside this element and stop the event bubbling up
                e.stopPropagation();
              }}
              className="max-h-[218px] overflow-y-auto overscroll-y-auto overflow-x-hidden pink-scroll"
            >
              {isLoading ? (
                <LoadingRenderer />
              ) : sections ? (
                sections.map((section) => {
                  const filteredOptions = section.options.filter(
                    (option) =>
                      option.label
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) ||
                      option.description
                        ?.toLowerCase()
                        .includes(searchValue.toLowerCase()),
                  );

                  if (filteredOptions.length === 0) return null;

                  return (
                    <CommandGroup
                      key={`section-${section.title}`}
                      className="mt-1 mb-2"
                    >
                      <Typography className="font-bold px-2 py-1">
                        {section.title}
                      </Typography>

                      {filteredOptions.map((option) => (
                        <CommandItem
                          key={`${section.title}-${option.label}`}
                          value={String(option.value)}
                          onSelect={() => handleSelect(String(option.value))}
                          className={cn(
                            "font-normal flex items-center hover:cursor-pointer hover:bg-gray-50 text-gray-900 pr-3 pl-4 py-2 h-full",
                          )}
                        >
                          {renderOption(option)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  );
                })
              ) : (
                <CommandGroup>
                  {searchableSelectOptions.options.length > 0 ? (
                    searchableSelectOptions.options.map((option) => (
                      <CommandItem
                        key={String(option.value)}
                        value={String(option.value)}
                        onSelect={() => handleSelect(String(option.value))}
                        className={cn(
                          "font-bold flex items-center hover:cursor-pointer hover:bg-gray-50 text-gray-900 py-2 h-full",
                        )}
                      >
                        {renderOption(option)}
                      </CommandItem>
                    ))
                  ) : (
                    <CommandEmpty>{noResultsText}</CommandEmpty>
                  )}
                </CommandGroup>
              )}
            </CommandList>
            <FooterRenderer />
          </Command>
        </PopoverContent>
      </Popover>

      {(error?.message || helperText) && (
        <InlineFeedbackGroup>
          {error?.message && (
            <InlineFeedback variant="critical" message={error.message} nested />
          )}
          {helperText && (
            <InlineFeedback variant="info" message={helperText} nested />
          )}
        </InlineFeedbackGroup>
      )}
    </div>
  );
};

export const SearchableSelect = _SearchableSelect;

export interface SearchableSelectTriggerButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "value"> {
  className?: string;
  placeholder?: string;
  value?: React.ReactNode;
  hasValue?: boolean;
  disabled?: boolean;
  error?: FieldError;
  variant?: "default" | "ghost";
  size?: "default" | "small";
  isOpen?: boolean;
  multi?: boolean;
  readonly?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

export const SearchableSelectTriggerButton: React.FC<
  SearchableSelectTriggerButtonProps
> = (props) => {
  const {
    className,
    placeholder,
    value,
    hasValue,
    disabled,
    error,
    variant = "default",
    size = "default",
    isOpen = false,
    multi = false,
    readonly,
    ref,
    ...restProps
  } = props;
  const buttonId = useId();

  return (
    <div className="flex flex-col space-y-1">
      <div
        className={cn(
          "relative flex items-center border rounded bg-white transition-colors cursor-pointer",
          error ? "border-red-500" : "border-gray-200",
          !disabled &&
            !error &&
            "hover:border-pink-200 focus-within:border-pink-300",
          disabled
            ? ["bg-gray-50 text-gray-700 border-gray-200", "cursor-not-allowed"]
            : ["hover:border-pink-200 focus-within:border-pink-300"],
          readonly && "hover:border-gray-200 !cursor-default",
          size === "small" ? "h-5.5" : "h-8",
          variant === "ghost" &&
            "border-none bg-white !h-auto hover:bg-gray-100",
          className,
        )}
      >
        <div className="relative flex-1 h-full">
          {variant !== "ghost" && (
            <label
              htmlFor={buttonId}
              className={cn(
                "absolute top-1/2 -translate-y-1/2 text-gray-700 transition-all pointer-events-none",
                hasValue && "!text-caption top-4",
                "left-4",
                size === "small" && hasValue && "hidden", // dont show placeholder on small size
              )}
            >
              {placeholder}
            </label>
          )}

          <button
            id={buttonId}
            ref={ref}
            disabled={disabled || readonly}
            type="button"
            className={cn(
              "w-full rounded !text-caption text-left flex items-center justify-end h-full",
              "focus-visible:outline-none",
              disabled
                ? "opacity-50 cursor-not-allowed"
                : "focus-visible:outline-none",
              readonly && "hover:cursor-default",
              !readonly && !disabled && "hover:border-pink-200",
              "font-title",
              variant === "ghost" && "p-2",
              variant !== "ghost" && hasValue && "pl-4 pt-[22px] pb-2",
              size === "small" && hasValue && "my-auto mb-3 !pt-2",
              className,
            )}
            {...restProps}
          >
            {variant === "ghost" ? (
              <div className="flex items-center flex-1 gap-1 w-[calc(100%-40px)]">
                <span
                  className={cn(
                    "whitespace-nowrap mx-1.5",
                    !multi && "font-bold",
                  )}
                >
                  {placeholder}
                </span>
                <TruncatedText>{value}</TruncatedText>
              </div>
            ) : (
              value
            )}

            {!readonly && (
              <Icon
                icon={isOpen ? "chevron-right" : "chevron-left"}
                size={size === "small" ? 16 : 20}
                className={cn(
                  "text-gray-500 transition-transform",
                  "-rotate-90",
                  variant === "ghost" ? "text-gray-600" : "text-primary",
                  size === "default" && variant !== "ghost" ? "mr-3" : "mr-2",
                )}
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
SearchableSelectTriggerButton.displayName = "SearchableSelectTriggerButton";
// ...existing code...
